/**
 * Keeps `properties[Levertijd]` in sync on theme forms, exposes it to bundle JSON
 * adds, and backfills it for third-party add-to-cart flows (e.g. Boost collection
 * grids) that call `/cart/add` without the property.
 *
 * Once the property is on the line item, cart, checkout, admin, and customer emails
 * that loop line item properties all show the same delivery status.
 */
window.FoxTheme = window.FoxTheme || {};

FoxTheme.levertijd = (function () {
  var SELECTOR = '.js-levertijd-property';
  var SCOPES = 'form, product-bundle-variant-selector';
  var PROPERTY_KEY = 'Levertijd';
  var productCache = {};

  function strings() {
    return FoxTheme.levertijdStrings || {};
  }

  function scopeFor(input) {
    return input.form || input.closest(SCOPES) || document;
  }

  function textForStatus(input, status) {
    switch (status) {
      case 'in-stock':
        return input.dataset.textInStock || strings().inStock || '';
      case 'backorder':
        return input.dataset.textBackorder || strings().backorder || '';
      case 'out-of-stock':
        return input.dataset.textOutOfStock || strings().outOfStock || '';
      default:
        return '';
    }
  }

  function statusFromVariant(variant) {
    if (!variant || !variant.inventory_management) return 'untracked';
    if (variant.inventory_quantity > 0) return 'in-stock';
    if (variant.inventory_policy === 'continue') return 'backorder';
    return 'out-of-stock';
  }

  function textFromVariant(variant, leadTime) {
    var status = statusFromVariant(variant);
    switch (status) {
      case 'in-stock':
        return strings().inStock || '';
      case 'backorder':
        return leadTime || strings().backorder || '';
      case 'out-of-stock':
        return strings().outOfStock || '';
      default:
        return '';
    }
  }

  function sync(input) {
    if (!input.dataset.variantStatus) return;

    var variantInput = scopeFor(input).querySelector('[name="id"]');
    if (!variantInput || !variantInput.value) return;

    var statuses;
    try {
      statuses = JSON.parse(input.dataset.variantStatus);
    } catch (error) {
      return;
    }

    var status = statuses[variantInput.value];
    if (!status) return;

    input.value = textForStatus(input, status);
    input.disabled = !input.value;
  }

  function syncAll(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(sync);
  }

  /**
   * Returns `{ properties: { Levertijd: '...' } }` for a container, or `{}` when the
   * container has no delivery status. Spread into a Cart AJAX API line item.
   */
  function propertiesFor(container) {
    if (!container) return {};

    var input = container.querySelector(SELECTOR);
    if (!input) return {};

    sync(input);
    if (!input.value) return {};

    return { properties: { Levertijd: input.value } };
  }

  function cartAddUrl() {
    return (FoxTheme.routes && FoxTheme.routes.cart_add_url) || '/cart/add';
  }

  function cartChangeUrl() {
    return (FoxTheme.routes && FoxTheme.routes.cart_change_url) || '/cart/change';
  }

  function isCartAddRequest(url) {
    try {
      var path = typeof url === 'string' ? url : url && url.url;
      if (!path) return false;
      return String(path).indexOf(cartAddUrl()) !== -1 || /\/cart\/add/.test(String(path));
    } catch (error) {
      return false;
    }
  }

  function getProperty(properties, key) {
    if (!properties) return '';
    if (Array.isArray(properties)) {
      var match = properties.find(function (entry) {
        return entry && (entry.name === key || entry.key === key || entry[0] === key);
      });
      if (!match) return '';
      return match.value || match[1] || '';
    }
    return properties[key] || '';
  }

  function withLevertijd(properties, value) {
    var next = {};
    if (properties && !Array.isArray(properties)) {
      Object.keys(properties).forEach(function (key) {
        next[key] = properties[key];
      });
    }
    next[PROPERTY_KEY] = value;
    return next;
  }

  function fetchProduct(handle) {
    if (!handle) return Promise.resolve(null);
    if (productCache[handle]) return productCache[handle];

    productCache[handle] = fetch('/products/' + encodeURIComponent(handle) + '.js')
      .then(function (response) {
        if (!response.ok) throw new Error('product fetch failed');
        return response.json();
      })
      .catch(function () {
        delete productCache[handle];
        return null;
      });

    return productCache[handle];
  }

  function leadTimeFromProduct(product) {
    if (!product || !product.tags) return '';
    // product.js does not expose metafields; theme forms already set the richer value.
    return '';
  }

  function resolveValueForLine(item) {
    if (!item || !item.handle) return Promise.resolve('');

    return fetchProduct(item.handle).then(function (product) {
      if (!product || !product.variants) return '';
      var variant = product.variants.find(function (entry) {
        return String(entry.id) === String(item.variant_id || item.id);
      });
      return textFromVariant(variant, leadTimeFromProduct(product));
    });
  }

  function updateLineProperties(item, value) {
    if (!item || !item.key || !value) return Promise.resolve(null);

    return fetch(cartChangeUrl() + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: item.key,
        quantity: item.quantity,
        properties: withLevertijd(item.properties, value),
      }),
    }).then(function (response) {
      return response.json();
    });
  }

  function normalizeAddResponse(parsed) {
    if (!parsed) return [];
    if (Array.isArray(parsed.items)) return parsed.items;
    if (parsed.key || parsed.variant_id) return [parsed];
    return [];
  }

  function ensureLinesHaveLevertijd(parsed) {
    var items = normalizeAddResponse(parsed);
    if (!items.length) return Promise.resolve(parsed);

    var updated = false;

    return Promise.all(
      items.map(function (item) {
        if (getProperty(item.properties, PROPERTY_KEY)) return Promise.resolve(item);

        return resolveValueForLine(item).then(function (value) {
          if (!value) return item;
          return updateLineProperties(item, value).then(function () {
            updated = true;
            item.properties = withLevertijd(item.properties, value);
            return item;
          });
        });
      })
    ).then(function () {
      if (updated) {
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { open: false },
          })
        );
      }
      return parsed;
    });
  }

  function installCartAddInterceptor() {
    if (window.__levertijdCartAddPatched) return;
    window.__levertijdCartAddPatched = true;

    var originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;

    window.fetch = function (input, init) {
      var request = originalFetch.apply(this, arguments);
      if (!isCartAddRequest(input)) return request;

      return request.then(function (response) {
        var clone = response.clone();
        return clone
          .json()
          .then(function (parsed) {
            if (!parsed || parsed.status) return response;
            return ensureLinesHaveLevertijd(parsed).then(function () {
              return response;
            });
          })
          .catch(function () {
            return response;
          });
      });
    };
  }

  document.addEventListener('change', function (event) {
    var target = event.target;
    if (!target || target.name !== 'id') return;

    var scope = target.form || target.closest(SCOPES);
    if (scope) scope.querySelectorAll(SELECTOR).forEach(sync);
  });

  document.addEventListener('shopify:section:load', function (event) {
    syncAll(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      syncAll();
      installCartAddInterceptor();
    });
  } else {
    syncAll();
    installCartAddInterceptor();
  }

  return {
    sync: sync,
    syncAll: syncAll,
    propertiesFor: propertiesFor,
    ensureLinesHaveLevertijd: ensureLinesHaveLevertijd,
  };
})();
